import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import GererPersonnelModal from '@/components/feature/GererPersonnelModal';
import BulkImportStaffModal from '@/components/feature/BulkImportStaffModal';

export default function ComptablesPage() {
  const { user, role, userRoles, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const isTrueSuperAdmin = userRoles?.includes('super_admin') ?? false;
  const [loading, setLoading] = useState(false);
  const [comptables, setComptables] = useState<any[]>([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    motDePasse: '',
    sexe: '',
  });
  const [loadingCreate, setLoadingCreate] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Gérer un comptable
  const [selectedComptable, setSelectedComptable] = useState<any>(null);
  const [showGererModal, setShowGererModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);

  useEffect(() => {
    if (authLoading) return;

    if (!user) {
      navigate('/login', { replace: true });
      return;
    }

    if (role !== 'super_admin' && role !== 'admin') {
      navigate('/', { replace: true });
      return;
    }

    loadComptables();
  }, [user, role, authLoading, navigate]);

  const loadComptables = async () => {
    setLoading(true);
    const { data, error: loadErr } = await supabase
      .from('comptables')
      .select('*')
      .order('created_at', { ascending: false });

    if (loadErr) {
      console.error('Erreur chargement comptables:', loadErr);
      setLoading(false);
      return;
    }

    if (data) {
      setComptables(data);
    }
    setLoading(false);
  };

  const createComptable = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoadingCreate(true);
    setError('');
    setSuccess('');

    try {
      const { data: authData } = await supabase.auth.getSession();
      if (!authData.session) {
        throw new Error('Session expirée');
      }

      const response = await supabase.functions.invoke('create-comptable', {
        body: {
          nom: formData.nom,
          prenom: formData.prenom,
          email: formData.email,
          telephone: formData.telephone || null,
          motDePasse: formData.motDePasse,
          sexe: formData.sexe || null,
        },
        headers: {
          Authorization: `Bearer ${authData.session.access_token}`,
        },
      });

      if (response.error) {
        const serverMsg = response.data?.error || response.error?.message || (typeof response.error === 'string' ? response.error : '');
        throw new Error(serverMsg || 'Erreur lors de la création du compte');
      }

      setSuccess('Comptable créé avec succès !');
      setFormData({ nom: '', prenom: '', email: '', telephone: '', motDePasse: '', sexe: '' });
      setShowCreateForm(false);
      loadComptables();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la création du compte');
      setTimeout(() => setError(''), 5000);
    } finally {
      setLoadingCreate(false);
    }
  };

  const toggleComptableStatus = async (comptableId: string, currentStatus: boolean) => {
    try {
      const newStatus = !currentStatus;
      const { error: updErr } = await supabase
        .from('comptables')
        .update({ bloque: newStatus })
        .eq('id', comptableId);

      if (updErr) throw updErr;

      setComptables(prev =>
        prev.map(c => c.id === comptableId ? { ...c, bloque: newStatus } : c)
      );

      setSuccess(newStatus ? 'Comptable bloqué avec succès' : 'Comptable débloqué avec succès');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError('Erreur lors de la modification du statut');
      setTimeout(() => setError(''), 3000);
    }
  };

  const deleteComptable = async (comptableId: string, userId: string) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer ce comptable ? Cette action est irréversible.')) return;

    try {
      const { data: authData } = await supabase.auth.getSession();
      if (!authData.session) {
        throw new Error('Session expirée');
      }

      const response = await supabase.functions.invoke('delete-comptable', {
        body: {
          comptableId,
          userId,
        },
        headers: {
          Authorization: `Bearer ${authData.session.access_token}`,
        },
      });

      if (response.error) throw response.error;

      setSuccess('Comptable supprimé avec succès');
      await loadComptables();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      const msg = err?.context?.error || err?.message || 'Erreur lors de la suppression du comptable';
      setError(msg);
      setTimeout(() => setError(''), 5000);
    }
  };

  const filteredComptables = comptables.filter(c => {
    const searchLower = searchQuery.toLowerCase();
    return (
      c.nom.toLowerCase().includes(searchLower) ||
      c.prenom.toLowerCase().includes(searchLower) ||
      c.email.toLowerCase().includes(searchLower) ||
      (c.telephone && c.telephone.toLowerCase().includes(searchLower))
    );
  });

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-4-line text-4xl text-clinic-blue animate-spin mb-4"></i>
          <div className="text-clinic-blue-dark text-xl">Chargement...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-3xl shadow-xl border-2 border-clinic-blue/10 overflow-hidden">
          <div className="p-8">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="text-3xl font-bold text-clinic-blue-dark">Gestion des Comptables</h1>
                <p className="text-gray-600 mt-2">Créez et gérez les comptes des comptables</p>
              </div>
              <button
                onClick={() => navigate('/admin')}
                className="px-4 py-2 text-clinic-blue hover:text-clinic-blue-dark transition-colors whitespace-nowrap"
              >
                ← Retour au tableau de bord
              </button>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-red-50 border-2 border-red-200 text-red-600 rounded-2xl flex items-start">
                <i className="ri-error-warning-line text-xl mr-2 mt-0.5"></i>
                <span>{error}</span>
              </div>
            )}

            {success && (
              <div className="mb-6 p-4 bg-green-50 border-2 border-green-200 text-green-600 rounded-2xl flex items-start">
                <i className="ri-check-line text-xl mr-2 mt-0.5"></i>
                <span>{success}</span>
              </div>
            )}

            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-clinic-blue-dark">
                Liste des comptables ({comptables.length})
              </h2>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowImportModal(true)}
                  className="px-6 py-3 bg-amber-500 text-white rounded-full hover:bg-amber-600 transition-all font-semibold whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-file-upload-line mr-2"></i>Importer (CSV)
                </button>
                <button
                  onClick={() => setShowCreateForm(true)}
                  className="px-6 py-3 bg-clinic-blue-dark text-white rounded-full hover:bg-clinic-blue transition-all font-semibold shadow-lg hover:shadow-xl whitespace-nowrap"
                >
                  <i className="ri-add-line mr-2"></i>
                  Créer un compte comptable
                </button>
              </div>
            </div>

            {showCreateForm && (
              <div className="mb-8 p-6 bg-clinic-blue/5 border-2 border-clinic-blue/20 rounded-2xl">
                <h3 className="text-lg font-semibold text-clinic-blue-dark mb-4">Nouveau compte comptable</h3>
                <form onSubmit={createComptable} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-clinic-blue-dark mb-2">Nom *</label>
                      <input
                        type="text"
                        required
                        value={formData.nom}
                        onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                        className="w-full px-4 py-3 border-2 border-clinic-blue/20 rounded-2xl focus:outline-none focus:border-clinic-blue text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-clinic-blue-dark mb-2">Prénom *</label>
                      <input
                        type="text"
                        required
                        value={formData.prenom}
                        onChange={(e) => setFormData({ ...formData, prenom: e.target.value })}
                        className="w-full px-4 py-3 border-2 border-clinic-blue/20 rounded-2xl focus:outline-none focus:border-clinic-blue text-sm"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-clinic-blue-dark mb-2">Sexe</label>
                    <select
                      value={formData.sexe}
                      onChange={(e) => setFormData({ ...formData, sexe: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-clinic-blue/20 rounded-2xl focus:outline-none focus:border-clinic-blue text-sm pr-8"
                    >
                      <option value="">Sélectionner</option>
                      <option value="Masculin">Masculin</option>
                      <option value="Féminin">Féminin</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-clinic-blue-dark mb-2">Email *</label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-clinic-blue/20 rounded-2xl focus:outline-none focus:border-clinic-blue text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-clinic-blue-dark mb-2">Téléphone</label>
                    <input
                      type="tel"
                      value={formData.telephone}
                      onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-clinic-blue/20 rounded-2xl focus:outline-none focus:border-clinic-blue text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-clinic-blue-dark mb-2">Mot de passe *</label>
                    <input
                      type="password"
                      required
                      minLength={6}
                      value={formData.motDePasse}
                      onChange={(e) => setFormData({ ...formData, motDePasse: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-clinic-blue/20 rounded-2xl focus:outline-none focus:border-clinic-blue text-sm"
                      placeholder="Minimum 6 caractères"
                    />
                  </div>
                  <div className="flex gap-4">
                    <button
                      type="submit"
                      disabled={loadingCreate}
                      className="px-6 py-3 bg-clinic-blue-dark text-white rounded-full hover:bg-clinic-blue transition-all font-semibold shadow-lg disabled:opacity-50 whitespace-nowrap"
                    >
                      {loadingCreate ? 'Création...' : 'Créer le compte'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowCreateForm(false)}
                      className="px-6 py-3 bg-gray-200 text-gray-700 rounded-full hover:bg-gray-300 transition-all font-semibold whitespace-nowrap"
                    >
                      Annuler
                    </button>
                  </div>
                </form>
              </div>
            )}

            <div className="mb-6">
              <div className="relative">
                <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-xl"></i>
                <input
                  type="text"
                  placeholder="Rechercher un comptable (nom, prénom, email, téléphone)..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 border-2 border-clinic-blue/20 rounded-2xl focus:outline-none focus:border-clinic-blue text-sm"
                />
              </div>
            </div>

            <div className="space-y-4">
              {filteredComptables.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  {searchQuery ? 'Aucun comptable trouvé' : 'Aucun comptable créé pour le moment'}
                </div>
              ) : (
                filteredComptables.map((comptable: any) => (
                  <div key={comptable.id} className="border-2 border-clinic-blue/10 rounded-2xl p-6 hover:shadow-lg transition-shadow">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center">
                        <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mr-4">
                          <i className="ri-calculator-line text-3xl text-teal-600"></i>
                        </div>
                        <div>
                          <h3 className="text-xl font-bold text-clinic-blue-dark">
                            {comptable.nom} {comptable.prenom}
                          </h3>
                          <p className="text-gray-600">{comptable.email}</p>
                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                            {comptable.sexe && (
                              <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm font-medium">
                                {comptable.sexe}
                              </span>
                            )}
                          </div>
                          {comptable.telephone && (
                            <p className="text-sm text-gray-500">
                              <i className="ri-phone-line mr-1"></i>
                              {comptable.telephone}
                            </p>
                          )}
                          <p className="text-sm text-gray-500">
                            <i className="ri-calendar-line mr-1"></i>
                            Créé le {new Date(comptable.created_at).toLocaleDateString('fr-FR')}
                          </p>
                          {comptable.bloque && (
                            <span className="inline-block mt-2 px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-medium">
                              Compte bloqué
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => { setSelectedComptable(comptable); setShowGererModal(true); }}
                          className="px-4 py-2 bg-clinic-blue/10 text-clinic-blue-dark rounded-full hover:bg-clinic-blue/20 font-medium transition-all whitespace-nowrap cursor-pointer"
                        >
                          <i className="ri-settings-3-line mr-1"></i>
                          Gérer
                        </button>
                        <button
                          onClick={() => toggleComptableStatus(comptable.id, comptable.bloque)}
                          className={`px-4 py-2 rounded-full font-medium transition-all whitespace-nowrap cursor-pointer ${
                            comptable.bloque
                              ? 'bg-green-100 text-green-700 hover:bg-green-200'
                              : 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                          }`}
                        >
                          <i className={`${comptable.bloque ? 'ri-lock-unlock-line' : 'ri-lock-line'} mr-1`}></i>
                          {comptable.bloque ? 'Débloquer' : 'Bloquer'}
                        </button>
                        {isTrueSuperAdmin && (
                          <button
                            onClick={() => deleteComptable(comptable.id, comptable.user_id)}
                            className="px-4 py-2 bg-red-100 text-red-700 rounded-full hover:bg-red-200 font-medium transition-all whitespace-nowrap cursor-pointer"
                          >
                            <i className="ri-delete-bin-line mr-1"></i>
                            Supprimer
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      <BulkImportStaffModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        staffType="comptable"
        onSuccess={loadComptables}
      />

      <GererPersonnelModal
        isOpen={showGererModal}
        onClose={() => { setShowGererModal(false); setSelectedComptable(null); }}
        type="comptable"
        personnel={selectedComptable || { id: '', user_id: '', nom: '', prenom: '', email: '' }}
        onUpdate={loadComptables}
      />

      <Footer />
    </div>
  );
}